import { ReactNode, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { isCoach } from "../lib/auth";
import { LoadingState } from "./Loading";

type RoleState = "checking" | "coach" | "not-coach";

/**
 * Route guard for coach-only screens.
 *
 * `RequireAuth` only asks "is anyone signed in", so every coach screen was reachable by any signed-in
 * user who typed the URL. Found by QA: a family account landed on /library -- the coach's exercise
 * manager -- and was shown Add exercise, and a pencil and trash icon on every row, while the nav above
 * still read Training / Account.
 *
 * Nothing could actually be changed: the API refuses those writes with 403, and that is the boundary
 * that matters. But offering a family controls that always fail, on a screen that is not theirs, is its
 * own defect -- it looks broken, and it advertises functionality they should not be thinking about.
 *
 * Sends them to their own home rather than the sign-in page: they ARE signed in, so bouncing them to a
 * login form would be its own confusion.
 */
export function RequireCoach({ children }: { children: ReactNode }) {
  const [state, setState] = useState<RoleState>("checking");

  useEffect(() => {
    let cancelled = false;
    isCoach()
      .then((coach) => {
        if (!cancelled) setState(coach ? "coach" : "not-coach");
      })
      // Fail closed. If the role cannot be established, showing the coach's screens is the wrong guess.
      .catch(() => {
        if (!cancelled) setState("not-coach");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (state === "checking") {
    return (
      <div className="dashboard">
        <LoadingState label="Checking your access…" />
      </div>
    );
  }

  if (state === "not-coach") {
    return <Navigate to="/family" replace />;
  }

  return <>{children}</>;
}
